import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { toast } from 'react-toastify';

// Interface pour les données utilisateur sécurisées
interface SecureUserData {
  uid: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  role: 'owner' | 'manager' | 'staff';
  restaurantId: string;
  lastLogin: any;
  isActive: boolean;
  permissions: string[];
}

// Interface pour les données de profil restaurant
interface RestaurantProfile {
  name: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  cuisine: string;
  priceRange: string;
  capacity: number;
  ownerId: string;
  isVerified: boolean;
  createdAt: any;
  updatedAt: any;
}

// Validation des entrées
class InputValidator {
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  static validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Le mot de passe doit contenir au moins 8 caractères');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins une lettre minuscule');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins une lettre majuscule');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins un chiffre');
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins un caractère spécial');
    }
    
    return { isValid: errors.length === 0, errors };
  }

  static sanitizeInput(input: string): string {
    return input.trim().replace(/[<>"'&]/g, '');
  }
}

// Service d'authentification sécurisé
export class SecureAuthService {
  private static instance: SecureAuthService;
  private currentUser: User | null = null;
  private userProfile: SecureUserData | null = null;
  private sessionTimeout: NodeJS.Timeout | null = null;
  private readonly SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

  private constructor() {
    this.initializeAuthListener();
  }

  static getInstance(): SecureAuthService {
    if (!SecureAuthService.instance) {
      SecureAuthService.instance = new SecureAuthService();
    }
    return SecureAuthService.instance;
  }

  private initializeAuthListener(): void {
    onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      
      if (user) {
        await this.loadUserProfile(user.uid);
        this.startSessionTimeout();
        await this.updateLastLogin(user.uid);
      } else {
        this.userProfile = null;
        this.clearSessionTimeout();
      }
    });
  }

  private async loadUserProfile(uid: string, retryCount: number = 0): Promise<void> {
    try {
      // Chargement du profil
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        this.userProfile = userDoc.data() as SecureUserData;
        // Profil chargé depuis Firestore
      } else {
        // Aucun document utilisateur trouvé
      }
    } catch (error: any) {
      // Gestion spécifique des erreurs Firestore
      if ((error.code === 'unavailable' || error.message?.includes('ERR_ABORTED')) && retryCount < 3) {
        // Importer et utiliser le service de santé
        import('./firestoreHealthService').then(({ default: healthService }) => {
          healthService.checkFirestoreHealth();
        });
        // Retry après un délai avec limite de tentatives
        setTimeout(() => {
          this.loadUserProfile(uid, retryCount + 1);
        }, 3000);
        return;
      }
      // Si trop de tentatives ou autre erreur, on continue sans bloquer
      console.error('Erreur lors du chargement du profil utilisateur:', error);
    }
  }

  private startSessionTimeout(): void {
    this.clearSessionTimeout();
    this.sessionTimeout = setTimeout(() => {
      this.logout(false);
      toast.warning('Session expirée. Veuillez vous reconnecter.');
    }, this.SESSION_DURATION);
  }

  private clearSessionTimeout(): void {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
  }

  private async updateLastLogin(uid: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid), {
        lastLogin: serverTimestamp()
      });
    } catch (error) {
      // Erreur non critique - ne pas bloquer la connexion
    }
  }

  // Connexion sécurisée
  async login(email: string, password: string): Promise<{ success: boolean; requiresVerification?: boolean }> {
    try {
      // Validation des entrées
      if (!InputValidator.validateEmail(email)) {
        throw new Error('Format d\'email invalide');
      }

      const sanitizedEmail = InputValidator.sanitizeInput(email);
      
      // Tentative de connexion
      const userCredential = await signInWithEmailAndPassword(auth, sanitizedEmail, password);
      const user = userCredential.user;

      // Notification de succès gérée par le composant Login
      return { success: true };
    } catch (error: any) {
      // Messages d'erreur sécurisés (pas de détails techniques)
      let errorMessage = 'Erreur de connexion';
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives. Réessayez plus tard.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'Compte désactivé';
      }
      
      throw new Error(errorMessage);
    }
  }

  // Inscription sécurisée
  async register(userData: {
    email: string;
    password: string;
    restaurantName: string;
    ownerName: string;
    phone: string;
    address: string;
  }): Promise<void> {
    try {
      // Validation des entrées
      if (!InputValidator.validateEmail(userData.email)) {
        throw new Error('Format d\'email invalide');
      }

      const passwordValidation = InputValidator.validatePassword(userData.password);
      if (!passwordValidation.isValid) {
        // Afficher le premier message d'erreur de validation
        const errorMessage = passwordValidation.errors[0];
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }

      // Sanitisation des données
      const sanitizedData = {
        email: InputValidator.sanitizeInput(userData.email),
        restaurantName: InputValidator.sanitizeInput(userData.restaurantName),
        ownerName: InputValidator.sanitizeInput(userData.ownerName),
        phone: InputValidator.sanitizeInput(userData.phone),
        address: InputValidator.sanitizeInput(userData.address)
      };

      // Création du compte
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        sanitizedData.email, 
        userData.password
      );
      const user = userCredential.user;

      // Mise à jour du profil
      await updateProfile(user, {
        displayName: sanitizedData.ownerName
      });

      // Création du profil utilisateur sécurisé
      const userProfile: SecureUserData = {
        uid: user.uid,
        email: sanitizedData.email,
        displayName: sanitizedData.ownerName,
        emailVerified: false,
        role: 'owner',
        restaurantId: '', // Sera défini après création du restaurant
        lastLogin: serverTimestamp(),
        isActive: true,
        permissions: ['read', 'write', 'admin']
      };

      await setDoc(doc(db, 'users', user.uid), userProfile);

      // Création du profil restaurant
      const restaurantProfile: RestaurantProfile = {
        name: sanitizedData.restaurantName,
        address: sanitizedData.address,
        phone: sanitizedData.phone,
        email: sanitizedData.email,
        cuisine: '',
        priceRange: '€€',
        capacity: 0,
        ownerId: user.uid,
        isVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const restaurantRef = doc(db, 'restaurants', user.uid);
      await setDoc(restaurantRef, restaurantProfile);

      // Mise à jour du restaurantId dans le profil utilisateur
      await updateDoc(doc(db, 'users', user.uid), {
        restaurantId: user.uid
      });

      // Forcer le rechargement du profil après création
      setTimeout(async () => {
        await this.loadUserProfile(user.uid);
      }, 100);

      // Envoi de l'email de vérification
      await sendEmailVerification(user);
      
      toast.success('Compte créé avec succès. Vérifiez votre email pour activer votre compte');
    } catch (error: any) {
      // Si l'erreur vient de la validation du mot de passe, ne pas afficher de toast supplémentaire
      if (error.message && (error.message.includes('mot de passe') || error.message.includes('caractère'))) {
        throw error;
      }
      
      let errorMessage = 'Erreur lors de la création du compte';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Cette adresse email est déjà utilisée';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Mot de passe trop faible';
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Déconnexion sécurisée
  async logout(showSuccessMessage: boolean = true): Promise<void> {
    try {
      this.clearSessionTimeout();
      
      // Nettoyer toutes les données sociales potentielles
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('social') || key.includes('instagram') || key.includes('twitter'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      await signOut(auth);
      this.currentUser = null;
      this.userProfile = null;
      if (showSuccessMessage) {
        toast.success('Déconnexion réussie');
      }
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  }

  // Réinitialisation du mot de passe
  async resetPassword(email: string): Promise<void> {
    try {
      if (!InputValidator.validateEmail(email)) {
        throw new Error('Format d\'email invalide');
      }

      const sanitizedEmail = InputValidator.sanitizeInput(email);
      await sendPasswordResetEmail(auth, sanitizedEmail);
      toast.success('Email de réinitialisation envoyé');
    } catch (error: any) {
      let errorMessage = 'Erreur lors de la réinitialisation';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Aucun compte associé à cette adresse email';
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Changement de mot de passe
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      if (!this.currentUser) {
        throw new Error('Utilisateur non connecté');
      }

      const passwordValidation = InputValidator.validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors.join(', '));
      }

      // Ré-authentification
      const credential = EmailAuthProvider.credential(
        this.currentUser.email!,
        currentPassword
      );
      await reauthenticateWithCredential(this.currentUser, credential);

      // Mise à jour du mot de passe
      await updatePassword(this.currentUser, newPassword);
      toast.success('Mot de passe mis à jour avec succès');
    } catch (error: any) {
      let errorMessage = 'Erreur lors du changement de mot de passe';
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe actuel incorrect';
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // Getters sécurisés
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  getUserProfile(): SecureUserData | null {
    return this.userProfile;
  }

  // Attendre que le profil utilisateur soit chargé
  async waitForUserProfile(maxWaitTime: number = 5000): Promise<SecureUserData | null> {
    if (this.userProfile) {
      return this.userProfile;
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkProfile = () => {
        if (this.userProfile) {
          resolve(this.userProfile);
        } else if (Date.now() - startTime > maxWaitTime) {
          resolve(null);
        } else {
          setTimeout(checkProfile, 100);
        }
      };
      checkProfile();
    });
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null && this.userProfile !== null;
  }

  hasPermission(permission: string): boolean {
    return this.userProfile?.permissions.includes(permission) || false;
  }

  // Rafraîchissement de session
  refreshSession(): void {
    if (this.isAuthenticated()) {
      this.startSessionTimeout();
    }
  }
}

// Export de l'instance singleton
export const authService = SecureAuthService.getInstance();